import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Billing() {
    const { user, authFetch } = useAuth();
    const navigate = useNavigate();
    const [billing, setBilling] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canceling, setCanceling] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchBilling();
    }, []);

    const fetchBilling = async () => {
        try {
            const res = await authFetch('/api/stripe/billing-history');
            if (res.ok) {
                const data = await res.json();
                setBilling(data.events || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const cancelSub = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of the current billing period.')) return;
        setCanceling(true);
        setError('');
        try {
            const res = await authFetch('/api/stripe/cancel-subscription', { method: 'POST' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setMessage(data.message);
        } catch (err) {
            setError(err.message);
        } finally {
            setCanceling(false);
        }
    };

    const openPortal = async () => {
        setPortalLoading(true);
        setError('');
        try {
            const res = await authFetch('/api/stripe/portal');
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            window.location.href = data.url;
        } catch (err) {
            setError(err.message);
            setPortalLoading(false);
        }
    };

    const checkout = async (plan) => {
        setUpgrading(true);
        setError('');
        try {
            const res = await authFetch('/api/stripe/create-checkout', {
                method: 'POST',
                body: JSON.stringify({ plan })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            window.location.href = data.url;
        } catch (err) {
            setError(err.message);
            setUpgrading(false);
        }
    };

    if (loading) return <LoadingSpinner fullPage text="Loading billing info..." />;

    const hasSubscription = user?.subscription_status === 'active';
    const plan = user?.subscription_plan;

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-extrabold" style={{ color: '#1a1a2e' }}>Billing & Subscription</h1>
                    <p className="text-gray-500 text-sm mt-0.5">Manage your plan, payments, and billing details</p>
                </div>
                <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className="fas fa-arrow-left text-xs"></i> Dashboard
                </Link>
            </div>

            {message && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700 text-sm">
                    <i className="fas fa-check-circle text-lg"></i>
                    <span>{message}</span>
                </div>
            )}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
                    <i className="fas fa-exclamation-circle text-lg"></i>
                    <span>{error}</span>
                </div>
            )}

            {/* Current Plan */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-4"><i className="fas fa-crown mr-2" style={{ color: '#e94560' }}></i>Current Plan</h3>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold`}
                                style={{ background: hasSubscription ? 'rgba(233,69,96,0.1)' : '#f0f0f0', color: hasSubscription ? '#e94560' : '#666' }}>
                                <div className={`w-2 h-2 rounded-full`} style={{ background: hasSubscription ? '#e94560' : '#ccc' }}></div>
                                {hasSubscription ? `${plan === 'monthly' ? 'Monthly' : 'Yearly'} Plan — Active` : 'No Active Subscription'}
                            </div>
                        </div>
                        {user?.subscription_end_date && (
                            <p className="text-sm text-gray-500">
                                {user.subscription_status === 'canceled' ? 'Access until' : 'Renews on'}: {new Date(user.subscription_end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                        )}
                        {!hasSubscription && <p className="text-sm text-gray-500">Purchase a plan to access daily report generation.</p>}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        {hasSubscription ? (
                            <>
                                <button onClick={openPortal} disabled={portalLoading}
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors">
                                    {portalLoading ? <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span> : <i className="fas fa-external-link-alt"></i>}
                                    Billing Portal
                                </button>
                                <button onClick={cancelSub} disabled={canceling}
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border-2 text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-60 transition-colors">
                                    {canceling ? <span className="w-4 h-4 border-2 border-red-200 border-t-red-500 rounded-full animate-spin"></span> : <i className="fas fa-times-circle"></i>}
                                    {canceling ? 'Canceling...' : 'Cancel Plan'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => checkout('monthly')} disabled={upgrading}
                                    className="px-5 py-2.5 rounded-xl font-bold text-sm text-white hover:opacity-90 disabled:opacity-60 transition-all"
                                    style={{ background: '#e94560' }}>
                                    Monthly — $29.99/mo
                                </button>
                                <button onClick={() => checkout('yearly')} disabled={upgrading}
                                    className="px-5 py-2.5 rounded-xl font-bold text-sm text-white hover:opacity-90 disabled:opacity-60 transition-all"
                                    style={{ background: '#1a1a2e' }}>
                                    Yearly — $299.99/yr
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Upgrade options (for monthly users) */}
            {hasSubscription && plan === 'monthly' && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                    <h3 className="font-bold text-gray-900 mb-3"><i className="fas fa-arrow-up mr-2" style={{ color: '#e94560' }}></i>Upgrade to Yearly & Save</h3>
                    <p className="text-sm text-gray-500 mb-4">Switch to yearly billing and save $59.89 compared to paying monthly.</p>
                    <button onClick={() => checkout('yearly')} disabled={upgrading}
                        className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white text-sm hover:opacity-90 disabled:opacity-60 transition-all"
                        style={{ background: '#1a1a2e' }}>
                        {upgrading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : <i className="fas fa-crown"></i>}
                        Upgrade to Yearly — $299.99/yr
                    </button>
                </div>
            )}

            {/* Plan options (non-subscribers) */}
            {!hasSubscription && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                    <h3 className="font-bold text-gray-900 mb-4"><i className="fas fa-tags mr-2" style={{ color: '#e94560' }}></i>Available Plans</h3>
                    <div className="grid sm:grid-cols-3 gap-3">
                        {[
                            { plan: 'onetime', label: 'One-Time Report', price: '$5.99', sub: '1 download · no subscription', color: '#27ae60' },
                            { plan: 'monthly', label: 'Monthly Plan', price: '$29.99/mo', sub: '4 reports/day · cancel anytime', color: '#e94560' },
                            { plan: 'yearly', label: 'Yearly Plan', price: '$299.99/yr', sub: '4 reports/day · save $59.89', color: '#1a1a2e' }
                        ].map(p => (
                            <button key={p.plan} onClick={() => checkout(p.plan)} disabled={upgrading}
                                className="p-4 rounded-xl border-2 text-left transition-all hover:shadow-md hover:border-red-200 disabled:opacity-60"
                                style={{ borderColor: '#e2e5ef' }}>
                                <div className="font-bold text-sm mb-1" style={{ color: p.color }}>{p.label}</div>
                                <div className="font-extrabold text-lg" style={{ color: '#1a1a2e' }}>{p.price}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{p.sub}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Billing History */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="font-bold text-gray-900"><i className="fas fa-history mr-2" style={{ color: '#e94560' }}></i>Billing History</h3>
                </div>
                {billing.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                        <i className="fas fa-receipt text-3xl mb-3 block opacity-40"></i>
                        <p className="text-sm">No billing history yet</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        <div className="grid grid-cols-4 px-6 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">
                            <span>Date</span>
                            <span>Plan</span>
                            <span>Status</span>
                            <span className="text-right">Amount</span>
                        </div>
                        {billing.map(b => (
                            <div key={b.id} className="grid grid-cols-4 px-6 py-4 text-sm hover:bg-gray-50 transition-colors">
                                <span className="text-gray-600">{new Date(b.createdAt).toLocaleDateString()}</span>
                                <span className="font-medium text-gray-800 capitalize">{b.plan || 'Plan'}</span>
                                <span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                        style={{ background: b.status === 'paid' ? 'rgba(39,174,96,0.15)' : 'rgba(231,76,60,0.15)', color: b.status === 'paid' ? '#27ae60' : '#e74c3c' }}>
                                        {b.status}
                                    </span>
                                </span>
                                <span className="text-right font-bold text-gray-900">${(b.amount || 0).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
